import axios from 'axios';
import { toast } from 'sonner';
import { requestAiConsent } from './aiConsent';
import { aiLimitMessage } from './aiLimit';

const PRODUCTION_ORIGIN = 'https://growdaily.fun';

/* Inside the Android/iOS app the page is served from the bundled assets
 * (https://localhost or capacitor://localhost). A build that forgot
 * REACT_APP_BACKEND_URL would send /api to that asset server, which answers
 * every request with index.html - so the app would look alive but empty
 * (Eden with no plot, no garden). The shell always talks to production. */
export function resolveBackendUrl(configured = process.env.REACT_APP_BACKEND_URL, loc = typeof window !== 'undefined' ? window.location : null) {
    const value = (configured || '').replace(/\/$/, '');
    if (value || !loc) return value;
    const nativeShell = loc.protocol === 'capacitor:' || loc.protocol === 'ionic:'
        || (loc.hostname === 'localhost' && !loc.port && loc.protocol === 'https:');
    return nativeShell ? PRODUCTION_ORIGIN : '';
}

export const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

const isNative = () => typeof window !== 'undefined'
    && !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

export const api = axios.create({
    baseURL: API,
    timeout: 20000,
    withCredentials: false, // safe default; web sets it true per-request below
});

// Always use Bearer token from localStorage — cookies disabled across the board
// for CORS simplicity (backend allow_credentials=False, required for Play Store).
api.interceptors.request.use((cfg) => {
    cfg.withCredentials = false;
    try {
        const tok = localStorage.getItem('session_token');
        if (tok && !cfg.headers.Authorization) cfg.headers.Authorization = `Bearer ${tok}`;
    } catch (e) { /* ignore */ }
    return cfg;
});

// ---- Signup CTA toast (guest / logged-out users trying to USE a feature) ----
const CTA_STRINGS = {
    da: {
        msg: 'Opret en gratis profil for at bruge denne funktion 🌱 Det tager under et minut.',
        signup: 'Opret profil',
    },
    en: {
        msg: 'Create a free profile to use this feature 🌱 It takes less than a minute.',
        signup: 'Sign up',
    },
};
let lastCtaAt = 0;
export function showSignupCta() {
    const now = Date.now();
    if (now - lastCtaAt < 4000) return; // throttle duplicate toasts
    lastCtaAt = now;
    let loc = 'en';
    try { loc = localStorage.getItem('locale') || 'en'; } catch { /* ignore */ }
    const s = CTA_STRINGS[loc] || CTA_STRINGS.en;
    toast(s.msg, {
        id: 'signup-cta',
        duration: 8000,
        action: {
            label: s.signup,
            onClick: () => { window.location.href = '/register'; },
        },
    });
}

// Catch guest/anonymous "usage" attempts globally and show the signup CTA.
api.interceptors.response.use(undefined, async (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const url = error?.config?.url || '';
    const method = (error?.config?.method || 'get').toLowerCase();
    if (status === 402 && detail?.code === 'ai_cost_confirmation' && !error.config?.__growgoldConfirmed) {
        const da = localStorage.getItem('locale') === 'da';
        if (!detail.affordable) {
            toast.error(da ? 'Du mangler GrowGold. Se saldo og pakker under Premium.' : 'Not enough GrowGold. View your balance and packs under Premium.');
            return Promise.reject(error);
        }
        const session = localStorage.getItem('session_token');
        if (!await requestAiConsent(detail)) return Promise.reject(new axios.CanceledError('AI purchase cancelled'));
        // Never submit a decision made before logout/account switching.
        if (!session || session !== localStorage.getItem('session_token') || error.config.headers.Authorization !== `Bearer ${session}`)
            return Promise.reject(new axios.CanceledError('Account changed; request a new AI price'));
        error.config.__growgoldConfirmed = true;
        error.config.headers['X-GrowGold-Quote'] = detail.quote_id;
        error.config.timeout = Math.max(error.config.timeout || 0, 180000);
        return api.request(error.config);
    }
    if ((error.config?.__growgoldConfirmed && (!error.response || status >= 500)) || detail?.code === 'ai_request_running') {
        const da = localStorage.getItem('locale') === 'da';
        toast.error(da ? 'AI-status er ikke bekræftet. Se AI-historik under Premium, før du prøver igen.' : 'AI status is not confirmed. Check AI history under Premium before trying again.', { duration: 10000 });
        // Do not automatically repeat a paid POST on uncertain network outcomes.
    }
    const limit = aiLimitMessage(error, localStorage.getItem('locale'));
    if (limit) toast.error(limit, { id: 'ai-monthly-limit', duration: 8000 });
    const isGuestBlock = status === 403 && typeof detail === 'string' && detail.startsWith('Opret en gratis profil');
    const isAnonAction = status === 401 && method !== 'get' && !url.includes('/auth/');
    if (isGuestBlock || isAnonAction) showSignupCta();
    const retryable = method === 'get' && !error.config?.__growdailyRetried
        && (!error.response || status >= 500);
    if (retryable) {
        error.config.__growdailyRetried = true;
        await new Promise((resolve) => setTimeout(resolve, 500));
        return api.request(error.config);
    }
    return Promise.reject(error);
});

export const fileUrl = (path) => {
    if (!path) return '';
    let tok = '';
    try { tok = localStorage.getItem('session_token') || ''; } catch { /* ignore */ }
    return `${API}/files/${path}${tok ? `?auth=${encodeURIComponent(tok)}` : ''}`;
};

export const pictureUrl = (value) => {
    if (!value) return '';
    if (/^(https?:|data:|blob:)/i.test(value)) return value;
    return fileUrl(value);
};

// Public CDN URL for files uploaded with ?public=true (community photos, app-studio images).
export const publicFileUrl = (path) => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    return `${API}/cdn/${path}`;
};
